import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { TrendingUp, Star, Save, Edit, Brain, Sparkles, Loader2, FileText, ChevronRight, Target, Award, AlertTriangle, Search, Users, Calendar, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Helper to format period - handles both date strings and text like "Q4 2025"
const formatPeriod = (period) => {
  if (!period) return 'N/A';
  // If it's already a text period like "Q4 2025", return as-is
  if (period.includes('Q') || period.includes('q')) {
    return period;
  }
  // Try to parse as date
  const date = new Date(period);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });
  }
  // Return original if can't parse
  return period;
};

export default function Performance() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allPerformance, setAllPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [insightDialogOpen, setInsightDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [performanceData, setPerformanceData] = useState({});
  const [aiInsight, setAiInsight] = useState(null);
  const [isGeneratingInsight, setIsGeneratingInsight] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [formData, setFormData] = useState({
    rating: 5,
    comments: '',
    period: new Date().toISOString().split('T')[0]
  });
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isManager = ['manager', 'department_head'].includes(user.role);
  const isHRAdmin = ['admin', 'hr_manager'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (isHRAdmin) {
        // HR Admin - fetch all employees and all performance reviews
        const [empRes, perfRes] = await Promise.all([
          axios.get(`${API}/employees`),
          axios.get(`${API}/performance`)
        ]);
        setEmployees(empRes.data);
        setTeamMembers(empRes.data);
        setAllPerformance(perfRes.data);
        
        // Group by employee
        const perfMap = {};
        perfRes.data.forEach(perf => {
          if (!perfMap[perf.employee_id]) perfMap[perf.employee_id] = [];
          perfMap[perf.employee_id].push(perf);
        });
        setPerformanceData(perfMap);
      } else if (isManager) {
        const teamRes = await axios.get(`${API}/my-team`);
        setTeamMembers(teamRes.data);
        
        const perfRes = await axios.get(`${API}/performance`);
        const perfMap = {};
        perfRes.data.forEach(perf => {
          if (!perfMap[perf.employee_id]) perfMap[perf.employee_id] = [];
          perfMap[perf.employee_id].push(perf);
        });
        setPerformanceData(perfMap);
      } else {
        const perfRes = await axios.get(`${API}/performance?employee_id=${user.employee_id}`);
        setPerformanceData({ [user.employee_id]: perfRes.data });
      }
    } catch (error) {
      toast.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  // HR Admin Analytics
  const hrAnalytics = useMemo(() => {
    if (!isHRAdmin) return null;
    
    const totalReviews = allPerformance.length;
    const avgRating = totalReviews > 0 
      ? (allPerformance.reduce((sum, p) => sum + (p.rating || 0), 0) / totalReviews).toFixed(1)
      : 0;
    
    const excellent = allPerformance.filter(p => p.rating >= 9).length;
    const good = allPerformance.filter(p => p.rating >= 7 && p.rating < 9).length;
    const average = allPerformance.filter(p => p.rating >= 5 && p.rating < 7).length;
    const needsImprovement = allPerformance.filter(p => p.rating < 5).length;
    
    const reviewedEmployees = new Set(allPerformance.map(p => p.employee_id)).size;
    const pendingReviews = employees.length - reviewedEmployees;
    
    return {
      totalReviews,
      avgRating,
      excellent,
      good,
      average,
      needsImprovement,
      reviewedEmployees,
      pendingReviews
    };
  }, [allPerformance, employees, isHRAdmin]);

  // Get unique years from performance data
  const availableYears = useMemo(() => {
    const years = new Set(allPerformance.map(p => new Date(p.period).getFullYear().toString()));
    return [...years].sort().reverse();
  }, [allPerformance]);

  // Filtered performance data for HR Admin
  const filteredPerformance = useMemo(() => {
    if (!isHRAdmin) return [];
    
    let filtered = allPerformance;
    
    // Filter by year
    if (selectedYear !== 'all') {
      filtered = filtered.filter(p => {
        const year = new Date(p.period).getFullYear().toString();
        return year === selectedYear;
      });
    }
    
    // Filter by rating
    if (ratingFilter !== 'all') {
      filtered = filtered.filter(p => {
        if (ratingFilter === 'excellent') return p.rating >= 9;
        if (ratingFilter === 'good') return p.rating >= 7 && p.rating < 9;
        if (ratingFilter === 'average') return p.rating >= 5 && p.rating < 7;
        if (ratingFilter === 'needs_improvement') return p.rating < 5;
        return true;
      });
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => {
        const employee = employees.find(e => e.employee_id === p.employee_id);
        return (
          p.employee_name?.toLowerCase().includes(query) ||
          p.employee_id?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query) ||
          p.reviewer_name?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered.sort((a, b) => new Date(b.period) - new Date(a.period));
  }, [allPerformance, selectedYear, ratingFilter, searchQuery, employees, isHRAdmin]);

  const openDialog = (employee) => {
    setSelectedEmployee(employee);
    const latestPerf = performanceData[employee.employee_id]?.[0];
    if (latestPerf) {
      setFormData({
        rating: latestPerf.rating,
        comments: latestPerf.comments || '',
        period: latestPerf.period
      });
    } else {
      setFormData({
        rating: 5,
        comments: '',
        period: new Date().toISOString().split('T')[0]
      });
    }
    setDialogOpen(true);
  };

  const generateAIInsight = async (employee) => {
    setSelectedEmployee(employee);
    setIsGeneratingInsight(true);
    setAiInsight(null);
    setInsightDialogOpen(true);

    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.post(
        `${API}/ai/performance-insights`,
        {
          employee_id: employee.employee_id,
          employee_name: employee.full_name,
          performance_data: performanceData[employee.employee_id] || []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAiInsight(response.data.insight);
        toast.success('AI insights generated successfully!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to generate AI insights';
      if (errorMsg.includes('AI features are disabled') || errorMsg.includes('AI service not configured')) {
        toast.info('AI insights are not enabled. Please enable AI in Settings > AI Configuration.');
      } else {
        toast.error(errorMsg);
      }
      setInsightDialogOpen(false);
    } finally {
      setIsGeneratingInsight(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/performance`, {
        employee_id: selectedEmployee.employee_id,
        employee_name: selectedEmployee.full_name,
        rating: parseInt(formData.rating),
        comments: formData.comments,
        period: formData.period,
        reviewer_id: user.employee_id,
        reviewer_name: user.full_name
      });
      toast.success('Performance updated successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update performance');
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 9) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (rating >= 7) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    if (rating >= 5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  };

  const getRatingLabel = (rating) => {
    if (rating >= 9) return 'Excellent';
    if (rating >= 7) return 'Good';
    if (rating >= 5) return 'Average';
    return 'Needs Improvement';
  };

  const getAverageRating = (employeeId) => {
    const ratings = performanceData[employeeId] || [];
    if (ratings.length === 0) return 0;
    const sum = ratings.reduce((acc, r) => acc + r.rating, 0);
    return (sum / ratings.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  // Employee view
  if (!isManager && !isHRAdmin) {
    const myPerformance = performanceData[user.employee_id] || [];
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Performance</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">View your performance reviews</p>
        </div>

        {myPerformance.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-12 text-center rounded-2xl">
            <TrendingUp className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No performance reviews yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {myPerformance.map((perf, index) => (
              <div key={index} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-2xl">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-lg ${getRatingColor(perf.rating)}`}>
                        <Star className="h-4 w-4 mr-1" />
                        {perf.rating}/10
                      </span>
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {formatPeriod(perf.period)}
                      </span>
                    </div>
                    {perf.comments && (
                      <p className="text-slate-700 dark:text-slate-300 mb-2">{perf.comments}</p>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Reviewed by: {perf.reviewer_name || 'Manager'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // HR Admin Analytics View
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Performance Analytics</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Monitor employee performance across the organization</p>
          </div>
          <div className="flex items-center space-x-2 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 px-4 py-2 border border-violet-200 dark:border-violet-800 rounded-xl">
            <Brain className="h-5 w-5 text-violet-600" />
            <span className="text-sm font-medium text-violet-700 dark:text-violet-300">AI-Powered Insights</span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <BarChart3 className="h-5 w-5 text-violet-200" />
              <span className="text-violet-100 text-sm font-medium">Average Rating</span>
            </div>
            <p className="text-4xl font-bold">{hrAnalytics?.avgRating || 0}</p>
            <p className="text-violet-200 text-sm mt-1">{hrAnalytics?.totalReviews || 0} total reviews</p>
          </div>

          <button
            onClick={() => setRatingFilter(ratingFilter === 'excellent' ? 'all' : 'excellent')}
            className={`p-5 rounded-2xl border-2 transition-all text-left ${
              ratingFilter === 'excellent' 
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-emerald-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Award className="h-5 w-5 text-emerald-500" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Excellent (9-10)</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{hrAnalytics?.excellent || 0}</p>
          </button>

          <button
            onClick={() => setRatingFilter(ratingFilter === 'good' ? 'all' : 'good')}
            className={`p-5 rounded-2xl border-2 transition-all text-left ${
              ratingFilter === 'good' 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Star className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Good (7-8)</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{hrAnalytics?.good || 0}</p>
          </button>

          <button
            onClick={() => setRatingFilter(ratingFilter === 'needs_improvement' ? 'all' : 'needs_improvement')}
            className={`p-5 rounded-2xl border-2 transition-all text-left ${
              ratingFilter === 'needs_improvement' 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-red-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Needs Improvement</span>
            </div>
            <p className="text-3xl font-bold text-red-600">{hrAnalytics?.needsImprovement || 0}</p>
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by employee name, ID, email, or reviewer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(e.target.value)}
              className="h-10 px-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <select 
              value={ratingFilter} 
              onChange={(e) => setRatingFilter(e.target.value)}
              className="h-10 px-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Ratings</option>
              <option value="excellent">Excellent (9-10)</option>
              <option value="good">Good (7-8)</option>
              <option value="average">Average (5-6)</option>
              <option value="needs_improvement">Needs Improvement (&lt;5)</option>
            </select>
          </div>
        </div>

        {/* Performance Reviews Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Performance Reviews from Managers</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{filteredPerformance.length} reviews found</p>
          </div>

          {filteredPerformance.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                {searchQuery ? 'No reviews found matching your search' : 'No performance reviews available'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Period</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Rating</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Reviewer</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Comments</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {filteredPerformance.map((perf, index) => {
                    const employee = employees.find(e => e.employee_id === perf.employee_id);
                    
                    return (
                      <tr key={`${perf.employee_id}-${index}`} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              perf.rating >= 9 ? 'bg-gradient-to-br from-emerald-400 to-teal-500' :
                              perf.rating >= 7 ? 'bg-gradient-to-br from-blue-400 to-indigo-500' :
                              perf.rating >= 5 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' :
                              'bg-gradient-to-br from-red-400 to-rose-500'
                            }`}>
                              <span className="text-white font-semibold text-sm">
                                {perf.employee_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-slate-100">{perf.employee_name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{perf.employee_id}</p>
                              <p className="text-xs text-slate-400">{employee?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-900 dark:text-slate-100">
                              {formatPeriod(perf.period)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-lg ${getRatingColor(perf.rating)}`}>
                              <Star className="h-4 w-4 mr-1" />
                              {perf.rating}/10
                            </span>
                            <span className="text-xs text-slate-500 mt-1">{getRatingLabel(perf.rating)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-900 dark:text-slate-100">{perf.reviewer_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{perf.reviewer_id}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate" title={perf.comments}>
                            {perf.comments || 'No comments'}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const emp = employees.find(e => e.employee_id === perf.employee_id);
                              if (emp) generateAIInsight(emp);
                            }}
                            className="h-8"
                          >
                            <Brain className="h-3 w-3 mr-1" />
                            AI Insight
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Employees Pending Review */}
        {hrAnalytics?.pendingReviews > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  {hrAnalytics.pendingReviews} employees have not been reviewed yet
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Remind managers to submit performance reviews for their team members.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* AI Insights Dialog */}
        <Dialog open={insightDialogOpen} onOpenChange={setInsightDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Brain className="h-5 w-5 mr-2 text-violet-600" />
                AI Performance Insights
                <Sparkles className="h-4 w-4 ml-2 text-yellow-500" />
              </DialogTitle>
            </DialogHeader>
            
            {isGeneratingInsight ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="relative">
                  <Loader2 className="h-12 w-12 text-violet-600 animate-spin" />
                  <Brain className="h-6 w-6 text-violet-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <p className="mt-4 text-slate-600 dark:text-slate-400 font-medium">Analyzing performance data...</p>
              </div>
            ) : aiInsight ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 p-4 border border-violet-200 dark:border-violet-800 rounded-2xl">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold">{selectedEmployee?.full_name?.charAt(0)}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100">{selectedEmployee?.full_name}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{selectedEmployee?.position} • {selectedEmployee?.department}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-5 border border-slate-200 dark:border-slate-700 rounded-2xl whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                  {aiInsight}
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setInsightDialogOpen(false)} className="rounded-xl">
                    Close
                  </Button>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Manager view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Team Performance</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage and track your team's performance with AI insights
          </p>
        </div>
        <div className="flex items-center space-x-2 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 px-4 py-2 border border-violet-200 dark:border-violet-800 rounded-xl">
          <Brain className="h-5 w-5 text-violet-600" />
          <span className="text-sm font-medium text-violet-700 dark:text-violet-300">AI-Powered Insights</span>
        </div>
      </div>

      {teamMembers.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-12 text-center rounded-2xl">
          <TrendingUp className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">No team members found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teamMembers.map((member) => {
            const avgRating = getAverageRating(member.employee_id);
            const latestPerf = performanceData[member.employee_id]?.[0];
            
            return (
              <div 
                key={member.employee_id} 
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 hover:shadow-xl transition-all duration-300 rounded-2xl"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">{member.full_name}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{member.position || 'N/A'}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{member.department || 'N/A'}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDialog(member); }} className="rounded-lg">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>

                <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                  {avgRating > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600 dark:text-slate-400">Average Rating</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 text-sm font-semibold rounded-lg ${getRatingColor(parseFloat(avgRating))}`}>
                          <Star className="h-3 w-3 mr-1" />
                          {avgRating}/10
                        </span>
                      </div>
                      {latestPerf && (
                        <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl">
                          <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">Latest Review</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-2">
                            {latestPerf.comments || 'No comments'}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                            {formatPeriod(latestPerf.period)}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">No reviews yet</p>
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDialog(member); }} className="rounded-lg">
                        <Star className="h-4 w-4 mr-2" />
                        Add Review
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <Button 
                    onClick={() => generateAIInsight(member)}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl"
                  >
                    <Brain className="h-4 w-4 mr-2" />
                    Generate AI Insights
                    <Sparkles className="h-3 w-3 ml-2" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Review Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Update Performance - {selectedEmployee?.full_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Review Period</Label>
              <Input
                type="date"
                value={formData.period}
                onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Rating (1-10)</Label>
              <div className="flex items-center space-x-4">
                <Input
                  type="range"
                  min="1"
                  max="10"
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                  className="flex-1"
                />
                <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-lg ${getRatingColor(parseInt(formData.rating))}`}>
                  {formData.rating}/10
                </span>
              </div>
            </div>
            <div>
              <Label>Comments</Label>
              <Textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Provide performance feedback..."
                rows={4}
                className="rounded-xl"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                <Save className="h-4 w-4 mr-2" />
                Save Review
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Insights Dialog */}
      <Dialog open={insightDialogOpen} onOpenChange={setInsightDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Brain className="h-5 w-5 mr-2 text-violet-600" />
              AI Performance Insights
              <Sparkles className="h-4 w-4 ml-2 text-yellow-500" />
            </DialogTitle>
          </DialogHeader>
          
          {isGeneratingInsight ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <Loader2 className="h-12 w-12 text-violet-600 animate-spin" />
                <Brain className="h-6 w-6 text-violet-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="mt-4 text-slate-600 dark:text-slate-400 font-medium">Analyzing performance data...</p>
            </div>
          ) : aiInsight ? (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 p-4 border border-violet-200 dark:border-violet-800 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">{selectedEmployee?.full_name?.charAt(0)}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">{selectedEmployee?.full_name}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{selectedEmployee?.position} • {selectedEmployee?.department}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 p-5 border border-slate-200 dark:border-slate-700 rounded-2xl whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                {aiInsight}
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setInsightDialogOpen(false)} className="rounded-xl">
                  Close
                </Button>
                <Button 
                  onClick={() => openDialog(selectedEmployee)}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Add Review
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
